
import {
  computed,
  defineComponent,
  inject,
  PropType,
  reactive, ref,
  Ref,
  SetupContext,
  onMounted,
  watch,
  nextTick,
  onBeforeUnmount,
} from "vue";
import { then, when } from "switch-ts";
import { ElTag, ElTooltip } from "element-plus";
import i18n from "@/core/i18n/i18n";

export enum ItemType {
  OBJECT,
  ARRAY,
  VALUE,
}

export type ValueTypes =
  | unknown
  | string
  | number
  | bigint
  | boolean
  | undefined;

export type ItemData = {
  key: string;
  type: ItemType;
  path: string;
  depth: number;
  length?: number;
  children?: ItemData[];
  value?: ValueTypes;
  parent?: ItemData;
};

export interface SelectedData {
  key: string;
  value: string;
  path: string;
}

export interface Data {
  [key: string]: string;
}

// Split `text` into plain / highlighted segments around each case-insensitive
// occurrence of `query`. Rendered as escaped text (never v-html), so
// highlighting cannot introduce XSS. Local to this component.
function highlightSegments(
  text: string,
  rawQuery: string
): { text: string; hit: boolean }[] {
  const query = rawQuery.trim();
  if (text === "" || query === "") {
    return [{ text, hit: false }];
  }
  const segments: { text: string; hit: boolean }[] = [];
  const haystack = text.toLowerCase();
  const needle = query.toLowerCase();
  let cursor = 0;
  for (;;) {
    const index = haystack.indexOf(needle, cursor);
    if (index === -1) {
      break;
    }
    if (index > cursor) {
      segments.push({ text: text.slice(cursor, index), hit: false });
    }
    segments.push({
      text: text.slice(index, index + needle.length),
      hit: true,
    });
    cursor = index + needle.length;
  }
  if (cursor < text.length) {
    segments.push({ text: text.slice(cursor), hit: false });
  }
  return segments.length > 0 ? segments : [{ text, hit: false }];
}

function isIdentifierBoundary(text: string, index: number): boolean {
  if (index <= 0 || index >= text.length) {
    return false;
  }
  const previous = text[index - 1];
  const current = text[index];
  const next = text[index + 1] || "";
  // Break opportunities: after a separator (_ . / -), at camelCase humps, and
  // before the last capital of an acronym (e.g. "JSONParser" -> "JSON|Parser").
  // Deliberately NOT between letters and digits, so alphanumeric suffixes like
  // "_L14", "v2", "utf8" stay intact instead of orphaning the digits.
  return (
    /[_./-]/.test(previous) ||
    (/[a-z]/.test(previous) && /[A-Z]/.test(current)) ||
    (/[A-Z]/.test(previous) &&
      /[A-Z]/.test(current) &&
      /[a-z]/.test(next))
  );
}

function addIdentifierBreaks(
  text: string,
  highlighted: { text: string; hit: boolean }[]
): { text: string; hit: boolean; breakBefore: boolean }[] {
  const result: { text: string; hit: boolean; breakBefore: boolean }[] = [];
  let globalIndex = 0;
  highlighted.forEach((segment) => {
    for (let index = 0; index < segment.text.length; index++, globalIndex++) {
      const breakBefore = isIdentifierBoundary(text, globalIndex);
      const previous = result[result.length - 1];
      if (!previous || previous.hit !== segment.hit || breakBefore) {
        result.push({
          text: segment.text[index],
          hit: segment.hit,
          breakBefore,
        });
      } else {
        previous.text += segment.text[index];
      }
    }
  });
  return result;
}

type Props = {
  data: ItemData;
  maxDepth: number;
  canSelect: boolean;
  objectMap: Map<any, any>;
  tab: number;
  path: string;
  element: SelectedElement;
  clickAbleDataType: "normal" | "branched";
  searchQuery: string;
  activeSearchPath: string;
  hasActiveSearchMatch: boolean;
  expandedPaths: Set<string>;
};

export type SelectedElement = {
  key: string;
  value: string;
}

export default defineComponent({
  name: "JsonTreeViewItem",
  props: {
    data: {
      required: true,
      type: Object as PropType<ItemData>,
    },
    maxDepth: {
      type: Number,
      required: false,
      default: 1,
    },
    canSelect: {
      type: Boolean,
      required: false,
      default: false,
    },
    objectMap: {
      type: Map,
      required: false,
      default: new Map(),
    },
    tab: {
      type: Number,
      required: false,
      default: 1,
    },
    path: {
      type: String,
      required: false,
      default: "path",
    },
    element: {
      type: Object as PropType<SelectedElement>,
      required: false,
      default: {
        key: "key",
        value: "value",
      }
    },
    clickAbleDataType: {
      type: String,
    },
    searchQuery: {
      type: String,
      required: false,
      default: "",
    },
    activeSearchPath: {
      type: String,
      required: false,
      default: "",
    },
    hasActiveSearchMatch: {
      type: Boolean,
      required: false,
      default: false,
    },
    // Node paths that must be force-open to reveal the active search match.
    // Layered on top of the user's own expand state so it never collapses
    // anything and fully reverts when search is cleared.
    expandedPaths: {
      type: Object as PropType<Set<string>>,
      required: false,
      default: () => new Set<string>(),
    },
  },
  components: {
    ElTag,
    ElTooltip
  },
  setup(props: Props, context: SetupContext) {
    const { t } = i18n.global;
    const state = reactive({
      open: props.data.depth < props.maxDepth,
    });

    // Depth past which indentation stops growing (provided by JsonTreeView based
    // on the live container width). Beyond it, children align with their parent
    // so content keeps a readable width instead of collapsing into slivers.
    const maxIndentLevels = inject<Ref<number>>(
      "jtvMaxIndentLevels",
      ref(999)
    );
    // A node at depth d indents its children (depth d+1). Stop once the children
    // would exceed the cap; otherwise inherit the adaptive per-level step.
    const branchIndentStyle = computed((): Record<string, string> =>
      props.data.depth < maxIndentLevels.value
        ? {}
        : { paddingLeft: "0px" }
    );

    const click = ref(0);
    let timer = ref();
    const isOverflowing = ref(false);

    const checkOverflow = () => {
      if (typeof props.data.value === 'string' && props.data.value.length > 100) {
        isOverflowing.value = true;
      } else {
        isOverflowing.value = false;
      }
    };

    const isSearching = computed(
      (): boolean => typeof props.searchQuery === "string" && props.searchQuery.length > 0
    );
    // Search force-opens the active match's ancestor chain, but only as an
    // overlay: the user's own `state.open` is never mutated, so clearing search
    // restores the exact pre-search expand/collapse state.
    const forcedOpen = computed(
      (): boolean =>
        isSearching.value &&
        !!props.expandedPaths &&
        props.expandedPaths.has(props.data.path)
    );
    const searchOpenOverride = ref<boolean | null>(null);
    const isOpen = computed((): boolean => {
      if (isSearching.value && searchOpenOverride.value !== null) {
        return searchOpenOverride.value;
      }
      return state.open || forcedOpen.value;
    });
    watch(
      () => [
        props.searchQuery,
        props.activeSearchPath,
        props.hasActiveSearchMatch,
      ],
      () => {
        // A new active result must be revealable even when the user manually
        // collapsed one of its ancestors during the previous navigation step.
        // Clearing search drops the temporary override and restores state.open.
        if (!isSearching.value || forcedOpen.value) {
          searchOpenOverride.value = null;
        }
      }
    );
    const isActiveMatch = computed(
      (): boolean =>
        isSearching.value &&
        props.hasActiveSearchMatch &&
        props.data.path === props.activeSearchPath
    );
    // Empty-string keys are legal JSON but render to nothing, so surface them
    // with a visible placeholder instead of a blank tag.
    function keyForDisplay(key: unknown): string {
      const text = key === null || key === undefined ? "" : String(key);
      return text === "" ? '""' : text;
    }
    const isEmptyKey = computed(
      (): boolean =>
        props.data.key === "" ||
        props.data.key === null ||
        props.data.key === undefined
    );
    const containerKeyText = computed((): string =>
      // Only the synthetic root (depth 0) is labelled OutputData; every other
      // node — including one with an empty key — shows its real (display) key.
      props.data.depth === 0 && (props.data.key === "/" || isEmptyKey.value)
        ? t("Flow.OutputData")
        : keyForDisplay(props.data.key)
    );
    const valueTextForDisplay = computed((): string =>
      props.data.value === null || props.data.value === undefined
        ? ""
        : String(props.data.value)
    );
    function segmentsFor(text: string) {
      return highlightSegments(text ?? "", props.searchQuery || "");
    }
    function keySegmentsFor(text: string) {
      const query = isSearching.value ? props.searchQuery || "" : "";
      return addIdentifierBreaks(
        text ?? "",
        highlightSegments(text ?? "", query)
      );
    }
    const showTooltip = ref(false);
    const valueTextRef = ref<HTMLElement|null>(null);

    function updateTooltipVisibility() {
      const el = valueTextRef.value;
      showTooltip.value = !!el && el?.scrollWidth > el.clientWidth;
    }

    onMounted(() => {
      nextTick(updateTooltipVisibility);
      window.addEventListener("resize", updateTooltipVisibility);
    });
    onBeforeUnmount(() => {
      window.removeEventListener("resize", updateTooltipVisibility);
      if (copyTimeout) clearTimeout(copyTimeout);
    });
    watch(() => props.data.value, () => nextTick(updateTooltipVisibility));
    onMounted(checkOverflow);
    
    watch(() => props.data.value, checkOverflow);

    const copied = ref(false);
    let copyTimeout: ReturnType<typeof setTimeout> | null = null;

    function getCopyText(): string {
      const el = valueTextRef.value;
      if (el && typeof el.textContent === "string") return el.textContent;
      return String(props.data.value ?? "");
    }

    async function copyValue() {
      try {
        const t = getCopyText();
        if (navigator.clipboard?.writeText) {
          await navigator.clipboard.writeText(t);
        } else {
          const ta = document.createElement("textarea");
          ta.value = t;
          ta.style.position = "fixed";
          ta.style.left = "-9999px";
          document.body.appendChild(ta);
          ta.focus();
          ta.select();
          document.execCommand("copy");
          document.body.removeChild(ta);
        }
        copied.value = true;
        if (copyTimeout) clearTimeout(copyTimeout);
        copyTimeout = setTimeout(() => (copied.value = false), 1200);
      }  catch (error) {
        console.log("error", error);
      }
    }
    function toggleOpen(data): void {
      click.value++;
      if (click.value === 1) {
        timer.value = setTimeout(() => {
          if (isSearching.value) {
            searchOpenOverride.value = !isOpen.value;
          } else {
            state.open = !state.open;
          }
          click.value = 0;
        }, 200);
      } else {
        clearTimeout(timer.value);
        click.value = 0;
        let path = removeAtFromPath(data.path);
        if (data.type === ItemType.ARRAY && data.parent?.type === ItemType.OBJECT) {
          context.emit("selected", {
            key: data.key,
            path: `${path}${data.key}`,
            value: "",
          });
        } else if (data.type === ItemType.ARRAY && data.parent?.type === ItemType.ARRAY) {
          context.emit("selected", {
            key: data.key,
            path: `${path}`,
            value: "",
          });
        } else if (data.type === ItemType.OBJECT && data.depth !== 0 && data.parent.type === ItemType.OBJECT) {
          context.emit("selected", {
            key: data.key,
            path: `${path}${data.key}`,
            value: "",
          });
        } else if (data.type === ItemType.OBJECT && data.depth !== 0 && data.parent.type === ItemType.ARRAY) {
          context.emit("selected", {
            key: data.key,
            path: `${path.replace(/@#$/, '')}`,
            value: "",
          });
        } else if (data.depth === 0 || data.type === ItemType.OBJECT) {
          context.emit("selected", {
            key: data.key,
            path: "/#",
            value: "",
          });
        }
      }
    }

    function setClass(tab) {
      if (tab === 4) {
        return "tag user-variable-tag";
      } else if (tab === 7) {
        return "tag system-variable-tag";
      } else if (tab === 6) {
        return "tag metaoutput-tag";
      } else {
        return "tag output-tag";
      }
    }

    function removeAtFromPath(path: string) {
      if (path.length > 1 && path.charAt(1) === "@") {
        return path.slice(0, 1) + path.slice(2);
      }
      return path;
    }

    function onClick(data: Data): void {
      context.emit("selected", {
        key: data.key,
        value: data.value,
        path: data.path,
      } as SelectedData);
    }

    function bubbleSelected(data: Data): void {
      context.emit("selected", data);
    }

    function getKey(itemData: ItemData): string {
      const keyValue = Number(itemData.key);
      return !isNaN(keyValue) ? `${itemData.key}":` : `"${itemData.key}":`;
    }

    function getValueColor(value: ValueTypes): string {
      return when(typeof value)
          .is((v) => v === "string", then("var(--jtv-string-color)"))
          .is((v) => v === "number", then("var(--jtv-number-color)"))
          .is((v) => v === "bigint", then("var(--jtv-number-color)"))
          .is((v) => v === "boolean", then("var(--jtv-boolean-color)"))
          .is((v) => v === "object", then("var(--jtv-null-color)"))
          .is((v) => v === "undefined", then("var(--jtv-null-color)"))
          .default(then("var(--jtv-valueKey-color)"));
    }

    function getKeyColor(path: string): string {
      if (props.tab === 1) {
        if (props.path === path) {
          return "var(--jtv-selectedKey-color) !important";
        } else {
          return (props.objectMap.has(path)) ? props.objectMap.get(path).color : "";
        }
      } else {
        let check = false;
        let color = "";
        props.objectMap.forEach((mapVal, mapKey) => {
          if (mapVal.value === path) {
            check = true;
            color = mapVal.color;
          }
        });
        return check ? color : "";
      }
    }

    function matchedElements(data) {
      if (props.tab === 1) {
        return props.element.key === data.path;
      } else {
        return props.element.value === data.path;
      }
    }

    const classes = computed((): unknown => ({
      "chevron-arrow": true,
      opened: isOpen.value,
    }));
    const valueClasses = computed((): unknown => ({
      "value-key": true,
      "can-select": props.canSelect,
    }));

    const lengthString = computed((): string => {
      const { length } = props.data;
      if (props.data.type === ItemType.ARRAY) {
        return length === 1 ? `(${length} item)` : `(${length} properties)`;
      }
      return length === 1 ? `(${length} item)` : `(${length} properties)`;
    });

    const dataValue = computed((): string => (props.data.value === undefined
        ? "undefined"
        : JSON.stringify(props.data.value)));

    return {
      state,
      isOverflowing,
      toggleOpen,
      onClick,
      bubbleSelected,
      getKey,
      getValueColor,
      classes,
      showTooltip,
      valueTextRef,
      valueClasses,
      lengthString,
      dataValue,
      ItemType,
      getKeyColor,
      matchedElements,
      setClass,
      copied,
      copyValue,
      t,
      isSearching,
      isActiveMatch,
      isOpen,
      containerKeyText,
      valueTextForDisplay,
      segmentsFor,
      keySegmentsFor,
      keyForDisplay,
      isEmptyKey,
      branchIndentStyle,
    };
  },
});
